import { useState } from "react";
import { ExternalLink, CreditCard, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { openBillingPortal } from "@/lib/billing";

export const BillingHistory = ({ history, error, onRetry }) => {
  const [busy, setBusy] = useState(false);
  const manage = async () => {
    setBusy(true);
    try { await openBillingPortal(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not open billing management"); setBusy(false); }
  };
  return (
    <section className="mb-10 border-y py-6" data-testid="billing-history">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="h-display text-lg font-semibold" data-testid="billing-history-title">Billing history</h2>
        {history?.has_billing_account && <Button variant="outline" onClick={manage} disabled={busy} className="gap-2" data-testid="manage-billing-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Manage billing
        </Button>}
      </div>
      {error ? <div role="alert" data-testid="billing-history-error" className="text-sm text-red-700">
        {error} <Button variant="link" onClick={onRetry} data-testid="billing-history-retry">Retry</Button>
      </div> : !history ? <p data-testid="billing-history-loading" className="text-sm text-muted-foreground">Loading invoices…</p>
        : history.invoices.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="billing-history-empty">No invoices yet.</p>
          : <div className="divide-y" data-testid="billing-invoice-list">{history.invoices.map((invoice) => (
            <div key={invoice.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`invoice-row-${invoice.id}`}>
              <div className="min-w-0">
                <p className="break-all text-sm font-medium" data-testid={`invoice-number-${invoice.id}`}>{invoice.number || "Subscription invoice"}</p>
                <p className="text-xs text-muted-foreground" data-testid={`invoice-date-${invoice.id}`}>{new Date(invoice.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold" data-testid={`invoice-amount-${invoice.id}`}>{new Intl.NumberFormat(undefined, { style: "currency", currency: invoice.currency }).format(invoice.status === "paid" ? invoice.amount_paid : invoice.amount_due)}</span>
                <span className="text-xs capitalize text-muted-foreground" data-testid={`invoice-status-${invoice.id}`}>{invoice.status}</span>
                {invoice.invoice_url && <a href={invoice.invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid={`invoice-open-${invoice.id}`}>View <ExternalLink className="h-3 w-3" /></a>}
                {invoice.pdf_url && <a href={invoice.pdf_url} target="_blank" rel="noopener noreferrer" aria-label="Download invoice PDF" title="Download invoice PDF" className="rounded p-2 hover:bg-secondary" data-testid={`invoice-pdf-${invoice.id}`}><Download className="h-4 w-4" /></a>}
              </div>
            </div>
          ))}</div>}
    </section>
  );
};